import { observer } from 'mobx-react';
import { makeAutoObservable } from 'mobx';
import { IEntityColumn, IEntityToken, ITrigger, IUserConfigPackage, TriggerEvaluationPoint } from '../../../API';
import ReusableDialog, { IReusableDialogFooterProps, ReusableDialogState } from '../../controls/ReusableDialog';
import { InfoTooltipLabel } from '../../controls/InfoTooltipLabel';
import { Dropdown, Stack } from '@fluentui/react';
import { useEffect } from 'react';
import ExpressionField, { ExpressionFieldState } from '../expression-builder/ExpressionField';
import TriggerActions, { TriggerActionsState } from '../trigger-actions/TriggerActions';
import { ExpressionBuilderOutputType } from '../expression-builder/ExpressionBuilder';

export class TriggerEditorState implements ITrigger {
	public userConfig: IUserConfigPackage;

	public dialog:ReusableDialogState;

	public Id: string = '';

	public TriggerExpression: string = '';
	public TriggerActionIds: string[] = [];
	public TriggerEvaluationPoint: TriggerEvaluationPoint = TriggerEvaluationPoint.BeforeEval;
	public expressionFieldState:ExpressionFieldState;
	public listUrl: string = '';

	//Unused
	//-------------
	public DisplayTitle: string = '';
	public ServerRelativeUrl: string = '';
	public NativeMetadataUrl: string = '';
	original: ITrigger = {} as ITrigger;

	triggerActions: TriggerActionsState;
	//-------------

	private thisItem() : ITrigger {
		return {
			Id: this.Id,
			TriggerExpression: this.TriggerExpression,
			DisplayTitle: this.DisplayTitle,
			ServerRelativeUrl: this.ServerRelativeUrl,
			NativeMetadataUrl: this.NativeMetadataUrl,
			TriggerActionIds: this.TriggerActionIds,
			TriggerEvaluationPoint: this.TriggerEvaluationPoint
		};
	}

	public dialogButtons: IReusableDialogFooterProps = {
		leftButtons: [/*
			{
				text: 'Delete',
				iconProps: {iconName: 'Delete'},
				onClick: (ev) => {
					this.dialog.onDismiss();
					this.onDelete(this.original);
				},
			}
		*/],
		rightButtons: [
			{
				text: 'Cancel',
				onClick: (ev) => {
					this.dialog.onDismiss();
				},
			},
			{
				text: 'Save',
				onClick: (ev) => {
					this.dialog.onDismiss();
					this.onSave(this.original, this.thisItem());
				},
			}
		],
	};

	public areTokensLoading: boolean = false;
	public tokens?: IEntityToken[];
	onSave: (original:ITrigger, updated:ITrigger) => void;
	onDelete: (profile:ITrigger) => void;

	constructor(
		userConfig: IUserConfigPackage,
		onDelete: (profile:ITrigger) => void,
		onSave: (original:ITrigger, updated:ITrigger) => void,
		listUrl: string = ''
	) {
		this.userConfig = userConfig;
		this.onDelete = onDelete;
		this.onSave = onSave;
		this.listUrl = listUrl;

		this.dialog = new ReusableDialogState("Trigger", 500);

		this.expressionFieldState = new ExpressionFieldState(ExpressionBuilderOutputType.Boolean, (updatedExpression) => {
			this.setTriggerExpression(updatedExpression);
		});

		this.triggerActions = new TriggerActionsState(userConfig, (ids) => {
			this.TriggerActionIds = ids;
		}, listUrl);

		makeAutoObservable(this);
	}

	public setListUrl(listUrl: string) {
		this.listUrl = listUrl;
		this.triggerActions.setListUrl(listUrl);
	}

	public setAvailableFields(listUrlColumns: IEntityColumn[]) {
		this.triggerActions.setAvailableFields(listUrlColumns);
	}

	public setTokens(tokens:IEntityToken[]) {
		this.tokens = tokens;
		this.expressionFieldState.setAvailableTokens(tokens);
		this.triggerActions.setTokens(tokens);
	}

	public setItem(item:ITrigger) {
		this.original = item;
		Object.assign(this, item);
		this.setEvaluationPoint(item.TriggerEvaluationPoint);
		this.expressionFieldState.setExpression(item.TriggerExpression);
		this.triggerActions.setSelected(item.TriggerActionIds || []);
	}

	public setEvaluationPoint(val: TriggerEvaluationPoint) {
		this.TriggerEvaluationPoint = val;
	}

	public setTriggerExpression(val:string) {
		this.TriggerExpression = val;
	}

	public async ensureInitialLoaded() : Promise<void> {
		
	}
}

function TriggerEditor({
	state
}: {
	state: TriggerEditorState
}) {

	useEffect(() => {
		state.ensureInitialLoaded();
	}, [state]);

	return (

		<ReusableDialog state={state.dialog} footerButtons={state.dialogButtons}>
			
			<Stack>

				<InfoTooltipLabel label='Evaluation Point' title='Select when the trigger condition should be evaluated in relation to other synchronisation activities. For example, you may want to send an email after everything else has been completed e.g. provisioning. Or, you may want to set some metadata values before provisioning occurs.'>
					<Dropdown
						options={[
							{ key: TriggerEvaluationPoint.BeforeEval, text: 'Before item synchronisation' },
							{ key: TriggerEvaluationPoint.AfterEval, text: 'After item synchronisation' }
						]}
						selectedKey={state.TriggerEvaluationPoint}
						onChange={(e, option) => {
							if (option) {
								state.setEvaluationPoint(option.key as TriggerEvaluationPoint);
							}
						}}
					/>
				</InfoTooltipLabel>

				<InfoTooltipLabel label='Condition' title='Enter an expression to determine when the actions will be executed.'>

					<ExpressionField state={state.expressionFieldState} />

				</InfoTooltipLabel>


				<InfoTooltipLabel label='Actions' title='Configure actions to execute when the condition is met.'>
					<TriggerActions state={state.triggerActions} />
				</InfoTooltipLabel>
				
			</Stack>
		</ReusableDialog>
	);
}

export default observer(TriggerEditor);
